import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ArrowRight, Clock, Search } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { ARTICLES, cornerstoneArticles } from "../lib/blogConfig";
import { NEIGHBORHOODS } from "../lib/neighborhoodConfig";
import LeadCaptureForm from "../components/LeadCaptureForm";

const FILTERS = [
  { id: "all",         label: "All Articles",    match: () => true },
  { id: "cornerstone", label: "Neighborhood Guides", match: (a) => a.type === "cornerstone" },
  { id: "best",        label: "Best Apartments", match: (a) => a.template === "best-apartments" },
  { id: "cost",        label: "Cost of Living",  match: (a) => a.template === "cost-of-living" },
  { id: "moving",      label: "Relocation",      match: (a) => a.template === "moving-to" },
  { id: "pet",         label: "Pet-Friendly",    match: (a) => a.template === "pet-friendly" },
  { id: "luxury",      label: "Luxury",          match: (a) => a.template === "luxury" },
];

const BlogIndex = () => {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useSEO({
    title: "Houston Apartment & Relocation Blog | Urban Nest Quest",
    description:
      "Houston neighborhood guides, relocation playbooks, and apartment-hunting advice from Urban Nest Quest — the locator team that knows the market block by block.",
    keywords: ["Houston Apartment Blog", "Houston Neighborhood Guides", "Moving to Houston", "Houston Relocation"],
    path: "/blog",
  });

  const items = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    const term = q.trim().toLowerCase();
    return ARTICLES.filter((a) => f.match(a))
      .filter((a) => !term || a.title.toLowerCase().includes(term) || a.metaDescription.toLowerCase().includes(term));
  }, [filter, q]);

  return (
    <div className="bg-[#FAFAF7] pt-20" data-testid="blog-index">
      {/* Hero */}
      <section className="bg-[#1F2A44] text-white">
        <div className="container-main py-12 sm:py-16">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="max-w-3xl">
            <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest text-[#C8A96B] border border-[#C8A96B]/40 font-semibold">
              <BookOpen className="w-3.5 h-3.5" /> Houston Apartment & Relocation Resources
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-['Playfair_Display'] leading-tight">
              Houston Neighborhood Guides &<br className="hidden sm:block" /> Relocation Playbooks
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">
              Real talk from locators who help renters find homes across Houston every year. No SEO sludge, no AI lorem — just the specifics you need to make your next move.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters + search */}
      <section className="container-main py-8">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="flex flex-wrap gap-2" data-testid="blog-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap ${
                  filter === f.id
                    ? "bg-[#1F2A44] text-white border-[#1F2A44]"
                    : "bg-white text-[#1F2A44] border-[#D9D9D9] hover:border-[#C8A96B]"
                }`}
                data-testid={`blog-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="sm:ml-auto relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#333333]/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#D9D9D9] bg-white text-sm focus:outline-none focus:border-[#C8A96B]"
              data-testid="blog-search"
            />
          </div>
        </div>
      </section>

      {/* Article grid */}
      <section className="container-main pb-12">
        <p className="text-xs text-[#333333]/60 mb-3">
          Showing <span className="font-semibold text-[#1F2A44]">{items.length}</span> articles
        </p>
        {items.length === 0 ? (
          <div className="p-10 rounded-2xl border border-[#D9D9D9] bg-white text-center text-sm text-[#333333]/70">
            No articles match that search. Try a different keyword.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {items.map((a) => {
              const n = NEIGHBORHOODS[a.neighborhood];
              return (
                <Link key={a.slug} to={`/blog/${a.slug}`}
                      className="group block p-6 rounded-2xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] hover:shadow-md transition-all"
                      data-testid={`blog-card-${a.slug}`}>
                  <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">
                    {a.type === "cornerstone" ? "Neighborhood Guide" : "Renter Resource"} · {n?.name}
                  </p>
                  <h2 className="mt-1.5 text-lg font-bold text-[#1F2A44] font-['Playfair_Display'] group-hover:text-[#C8A96B] transition-colors leading-snug">
                    {a.title}
                  </h2>
                  <p className="mt-2 text-xs text-[#333333]/75 line-clamp-3">{a.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-[11px] text-[#333333]/60">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {a.readingMinutes} min read
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#C8A96B] font-semibold">
                      Read article <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Lead capture footer band */}
      <section className="bg-white border-t border-[#D9D9D9]">
        <div className="container-main py-12 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                Skip the reading. Get hand-picked matches.
              </h2>
              <p className="mt-3 text-[#333333]/80 max-w-lg">
                Tell us what you're looking for and we'll text 3 hand-picked Houston apartment matches — complete with current move-in specials and available tour times.
              </p>
            </div>
            <LeadCaptureForm source="blog_index" submitLabel="Get My 3 Matches" />
          </div>
        </div>
      </section>
    </div>
  );
};

export const featuredCornerstoneArticles = cornerstoneArticles;

export default BlogIndex;
